import * as React from 'react';

import styled from '@emotion/styled';
import { Autocomplete, Creatable } from '@lightspeed/flame/Autocomplete';
import { Bone } from '@lightspeed/flame/Bone';
import $ from 'jquery';

interface Props {
  className?: string;
  initialOptions?: AutocompleteOption[];
  isDirtyOnChange?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isMulti?: boolean;
  isSearchable?: boolean;
  name: string;
  onCreateOption?: (newOption: string) => Promise<AutocompleteOption[]>;
  options?: AutocompleteOption[];
  placeholder?: string;
  selectAllOption?: boolean;
  selectAllText?: string;
  selectAllValue?: number;
}

interface Translations {
  createLabel: string;
  placeholder: string;
  selectAllText: string;
}

const DEFAULT_SELECT_ALL_OPTION_VALUE = -1;

const getStyleObj = ({ name }: { name: string }) => ({
  [`.${name}__value-container`]: {
    padding: 0,

    [`> div:not(.${name}__multi-value)`]: {
      padding: 0,
    },
  },

  [`.${name}__multi-value__label`]: {
    padding: '1px',
    paddingLeft: '4px',
  },
});

const StyledAutocomplete = styled(Autocomplete)(({ name }) => getStyleObj({ name }));

const StyledCreatable = styled(Creatable)(({ name }) => getStyleObj({ name }));

const DiscountRuleDropDown: React.FC<Props> = ({
  className = '',
  initialOptions = [],
  isDirtyOnChange = true,
  isDisabled = false,
  isLoading = false,
  isMulti = false,
  isSearchable = true,
  name,
  onCreateOption,
  options = [],
  placeholder = 'Select an option',
  selectAllOption = false,
  selectAllText = 'All',
  selectAllValue = DEFAULT_SELECT_ALL_OPTION_VALUE,
}) => {
  const [selectedItems, setSelectedItems] = React.useState<AutocompleteOption[]>([]);
  const [menuIsOpen, setMenuIsOpen] = React.useState<boolean | undefined>(undefined);
  const [translations, setTranslations] = React.useState<Translations>({
    createLabel: 'Create',
    placeholder,
    selectAllText,
  });

  const selectAllItem: AutocompleteOption = {
    value: selectAllValue,
    label: translations.selectAllText,
  };

  const listOfOptions = selectAllOption ? [selectAllItem, ...options] : options;

  const isAllSelected = (inputItem: AutocompleteOption): boolean =>
    selectAllOption && inputItem.value === selectAllValue;

  const inputId = !isMulti ? `${name}_input_field` : undefined;

  const handleIsOptionSelectedMulti = (
    inputItem: AutocompleteOption,
    inputItems: AutocompleteOption[]
  ) => {
    if (selectAllOption) {
      if (inputItems.some(isAllSelected)) {
        return true;
      }
    }
    return inputItems.some(item => item.value === inputItem.value);
  };

  const handleOnChange = async (inputSelectedItems: AutocompleteOption[], actionMeta: any) => {
    const { action, option, removedValue } = actionMeta;

    switch (action) {
      case 'select-option':
        if (isMulti) {
          const shouldSelectAll =
            isAllSelected(option) ||
            (selectAllOption && inputSelectedItems.length === listOfOptions.length - 1);

          if (shouldSelectAll) {
            setSelectedItems([selectAllItem]);
            break;
          }

          setSelectedItems(prevItems => [...prevItems, option]);
          break;
        }

        setSelectedItems(inputSelectedItems);
        break;

      case 'deselect-option':
        // eslint-disable-next-line no-underscore-dangle
        if (option.__isNew__ && onCreateOption) {
          const postResponse = await onCreateOption(option.value);
          setSelectedItems(postResponse);
          break;
        }

        if (inputSelectedItems.some(isAllSelected)) {
          setSelectedItems([option]);
          break;
        }

        setSelectedItems(prevItems => prevItems.filter(item => item.value !== option.value));
        break;

      case 'remove-value':
        if (isAllSelected(removedValue)) {
          setSelectedItems([]);
          break;
        }

        setSelectedItems(prevItems => prevItems.filter(item => item.value !== removedValue.value));
        break;

      case 'clear':
        setSelectedItems([]);
        break;

      default:
        break;
    }

    if (isDirtyOnChange) {
      window.viewFieldChanged('view');
    }
  };

  const handleOnFocus = () => {
    if (isGriffin) {
      setMenuIsOpen(true);
    }
  };

  const closeMenu = (event: JQuery.MouseDownEvent) => {
    const { target } = event;

    if (!target?.classList?.contains(internalClassName)) {
      setMenuIsOpen(false);
    }
  };

  const isGriffin = window.isGriffin();

  const internalClassName = `${name}__option`;

  const componentProps = {
    className: `notranslate ${internalClassName} ${className}`,
    classNamePrefix: name,
    closeMenuOnSelect: !isMulti,
    formatCreateLabel: (newValue: string) => `${translations.createLabel} ${newValue}`,
    hideSelectedOptions: false,
    inputId,
    isDisabled,
    isMulti,
    isOptionSelected: isMulti && handleIsOptionSelectedMulti,
    isSearchable,
    menuIsOpen,
    name,
    onChange: handleOnChange,
    onCreateOption,
    onFocus: handleOnFocus,
    options: listOfOptions,
    pageSize: 7,
    placeholder: translations.placeholder,
    value: selectedItems,
  };

  React.useEffect(() => {
    if (initialOptions.length) {
      setSelectedItems(initialOptions);
    } else if (selectAllOption) {
      setSelectedItems([selectAllItem]);
    }

    if (isGriffin) {
      // @ts-ignore
      $(document).on('click.discountRules', closeMenu);
    }

    Object.keys(translations).forEach(keyToTranslate => {
      const textToTranslate = translations[keyToTranslate as keyof Translations];
      window.Localize?.translate(textToTranslate, null, returnedTranslation => {
        const newTranslations = {
          ...translations,
          [`${keyToTranslate}`]: returnedTranslation.toString(),
        };
        setTranslations(newTranslations);
      });
    });

    return () => {
      if (isGriffin) {
        $(document).off('click.discountRules');
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    // Single-selection drop-downs will use the `react-select` input field that
    // does not have the `view_view` class, used to populate the AJAX request form body `key_values` key
    if (!isMulti) {
      $(`input[name="${name}"]`).addClass('view_view');
    }
  });

  if (isLoading) {
    return <Bone height={3} />;
  }

  return (
    <>
      {onCreateOption ? (
        <StyledCreatable {...componentProps} />
      ) : (
        <StyledAutocomplete {...componentProps} />
      )}
      {isMulti && (
        <input
          className="view_view isValueJsonEncoded"
          id={name}
          name={name}
          type="hidden"
          value={JSON.stringify({ ...selectedItems.map(({ value }) => value) })}
        />
      )}
    </>
  );
};

export default DiscountRuleDropDown;
